import { beginObservabilityContext } from './context';
import { normalizeError, normalizePath } from './privacy';
import { emitTelemetry } from './telemetry';

type RouterState = {
  location: { key: string; pathname: string };
  navigation: { state: string };
};

type ObservableRouter = {
  state: RouterState;
  subscribe: (listener: (state: RouterState) => void) => () => void;
};

export function reportBrowserError(source: string, error: unknown, fields: Record<string, boolean | number | string> = {}) {
  return emitTelemetry('frontend.error', { ...fields, ...normalizeError(error), source });
}

export function initializeGlobalErrorReporting(target: Window = window): () => void {
  const onError = (event: ErrorEvent) => {
    reportBrowserError('window', event.error ?? event.message, {
      column: event.colno,
      line: event.lineno,
    });
  };
  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    reportBrowserError('unhandled-rejection', event.reason);
  };

  target.addEventListener('error', onError);
  target.addEventListener('unhandledrejection', onUnhandledRejection);

  return () => {
    target.removeEventListener('error', onError);
    target.removeEventListener('unhandledrejection', onUnhandledRejection);
  };
}

export function initializeRouterObservability(router: ObservableRouter): () => void {
  let currentLocationKey = router.state.location.key;
  let navigationStartedAt: number | undefined;
  const initialContext = beginObservabilityContext(router.state.location.pathname);

  emitTelemetry('frontend.navigation', { navigationType: 'initial', route: initialContext.route }, initialContext);

  return router.subscribe(state => {
    if (state.navigation.state !== 'idle') {
      navigationStartedAt ??= performance.now();
      return;
    }
    if (state.location.key === currentLocationKey) return;

    currentLocationKey = state.location.key;
    const context = beginObservabilityContext(state.location.pathname);
    emitTelemetry(
      'frontend.navigation',
      {
        durationMs: navigationStartedAt === undefined ? 0 : Math.round(performance.now() - navigationStartedAt),
        navigationType: 'route',
        route: normalizePath(state.location.pathname),
      },
      context
    );
    navigationStartedAt = undefined;
  });
}

export function initializeBrowserObservability(router: ObservableRouter): () => void {
  const stopErrors = initializeGlobalErrorReporting();
  const stopRouter = initializeRouterObservability(router);
  return () => {
    stopErrors();
    stopRouter();
  };
}
